import { BookOpen, ChevronRight, FlaskConical } from 'lucide-react-native';
import { type ComponentType } from 'react';
import { twJoin } from 'tailwind-merge';

import { Badge, BadgeIcon, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import {
  trackProfileClinicalTrialsSelected,
  trackProfilePublicationsSelected,
} from '@/util/analyticsEvents';

type SectionTarget = 'publications' | 'trials';

interface ProfileChipsProps {
  expertId: number;
  /**
   * Number of publications attributed to this expert. Sourced from the public
   * user profile endpoint so the chips never trigger an extra fetch.
   */
  publicationsCount: number;
  /** Number of clinical trials attributed to this expert; same provenance. */
  clinicalTrialsCount: number;
  /**
   * Pass to make each chip a link that scrolls to its matching section below.
   * Omit on surfaces where those sections are not rendered (e.g. the public
   * profile) and the chips become plain stat pills with no arrow or press.
   */
  onSelect?: (target: SectionTarget) => void;
  /**
   * Whether to render the trailing chevron when chips are interactive.
   * Default `true`. Pass `false` on tight surfaces (e.g. the mobile inquiry
   * card) where the extra glyph pushes chips off the row.
   */
  showChevron?: boolean;
  className?: string;
}

const pluralize = (count: number, singular: string) =>
  `${count} ${count === 1 ? singular : `${singular}s`}`;

/**
 * Squared off to `rounded-md` rather than a pill, and given a border dark enough to see.
 *
 * These chips carry a chevron and navigate, which makes them buttons, and `rounded-md` is
 * this app's button shape. As pills they sat directly beneath the Reference Champion badge
 * at the same size on the same surface in a different geometry, so two adjacent controls
 * spoke two shape languages and the difference tracked nothing a reader could learn from.
 *
 * The blue stays. It is the only cue that these are pressable, and it does that work
 * precisely because the badge above them carries no hue: blue reads as navigation, neutral
 * as recognition. Only the border moves, from the `border-info-300` the `info` action
 * supplies to `info-600`, which lifts the boundary from 1.86:1 to 3.41:1 against the
 * `background-50` card and clears the 3:1 WCAG SC 1.4.11 asks of a control boundary. The
 * fill is `background-0`, white in the light theme, matching the Reference Champion card
 * above: `background-info` sat at 1.00:1 against the header, so it added a tint without an
 * edge, and a blue outline on white reads as a control where the blue-filled specialty
 * badge reads as a tag.
 *
 * Overridden here rather than on the shared `info` badge action, which would retint
 * specialty tags and directory cards in the same stroke.
 */
const BADGE_CLASS = 'gap-1.5 self-start rounded-md border-info-600 bg-background-0 px-2.5 py-1.5';

const BADGE_TEXT_CLASS = 'normal-case font-medium text-xs text-info-800';

/**
 * Header chips showing an expert's publication and clinical trial counts.
 * Hidden when both counts are zero so we never undersell the expert with a
 * "0" pill.
 *
 * Counts come from the user profile payload, so the chips render synchronously
 * with the rest of the header. When `onSelect` is provided the chips act as
 * links to the matching section; without it they render as plain stat pills.
 *
 * Visual: composes Gluestack `Badge` (action="info" variant="outline") inside
 * a `Pressable`, with the shape and border overridden locally. See
 * `BADGE_CLASS` for why these read as buttons rather than as tags.
 */
export const ProfileChips = ({
  expertId,
  publicationsCount,
  clinicalTrialsCount,
  onSelect,
  showChevron = true,
  className,
}: ProfileChipsProps) => {
  if (publicationsCount === 0 && clinicalTrialsCount === 0) {
    return null;
  }

  const renderChip = (target: SectionTarget, icon: ComponentType, label: string) => {
    const chipContent = (
      <Badge action="info" variant="outline" size="md" className={BADGE_CLASS}>
        <BadgeIcon as={icon} className="text-info-700" aria-hidden />

        <BadgeText className={BADGE_TEXT_CLASS}>{label}</BadgeText>

        {onSelect && showChevron ? (
          <BadgeIcon as={ChevronRight} className="text-info-700" aria-hidden />
        ) : null}
      </Badge>
    );

    if (!onSelect) {
      return <HStack {...getAccessibilityProps({ label, role: 'none' })}>{chipContent}</HStack>;
    }

    return (
      <Pressable
        onPress={() => {
          onSelect(target);

          if (target === 'publications') {
            trackProfilePublicationsSelected({ expertId });
          } else {
            trackProfileClinicalTrialsSelected({ expertId });
          }
        }}
        hitSlop={getAccessibleHitSlop(30)}
        className="self-start web:cursor-pointer web:transition-colors web:hover:opacity-80"
        {...getAccessibilityProps({
          role: 'button',
          label: `View ${label}`,
          hint: 'Scrolls to the section below',
        })}>
        {chipContent}
      </Pressable>
    );
  };

  return (
    <HStack className={twJoin('flex-wrap items-start gap-2', className)}>
      {publicationsCount > 0
        ? renderChip('publications', BookOpen, pluralize(publicationsCount, 'Publication'))
        : null}

      {clinicalTrialsCount > 0
        ? renderChip('trials', FlaskConical, pluralize(clinicalTrialsCount, 'Clinical Trial'))
        : null}
    </HStack>
  );
};
